/**
 *  @class
 *  @function InvoiceFields
 */
if (!customElements.get('invoice-fields')) {
  class InvoiceFields extends HTMLElement {
    constructor() {
      super();
      this.ATTR_KEYS = [
        'invoice_requested',
        'invoice_company_name',
        'invoice_tax_id',
        'invoice_address1',
        'invoice_address2',
        'invoice_postal_code',
        'invoice_city',
        'invoice_province_code',
        'invoice_country_code',
      ];
      this.MAX_ATTEMPTS = 3;
      this.savedSnapshot = null;
      this.isSaving = false;
      this.bypassIntercept = false;
    }

    connectedCallback() {
      this.append = this.dataset.append || '';
      this.checkbox = this.querySelector(`#InvoiceRequested${this.append}`);
      this.fieldset = this.querySelector(`#InvoiceFieldset${this.append}`);
      if (!this.checkbox || !this.fieldset) return;

      this.checkoutButton = this.parentNode.querySelector('.button.checkout-button');
      this.countrySelect = this.querySelector(`#InvoiceCountry${this.append}`);
      this.provinceSelect = this.querySelector(`#InvoiceProvince${this.append}`);
      this.provinceWrapper = this.querySelector(`#InvoiceProvinceWrapper${this.append}`);
      this.saveErrorEl = this.querySelector('.invoice-fields__save-error');
      this.retryBtn = this.querySelector('.invoice-fields__retry');
      this.continueWithoutBtn = this.querySelector('.invoice-fields__continue-without');
      this.inputs = Array.prototype.slice.call(this.querySelectorAll('.invoice-fields__input'));

      this.initProvinces();

      this.checkbox.addEventListener('change', () => this.onToggle());

      if (this.checkoutButton) {
        this.checkoutButton.addEventListener('click', (e) => this.onCheckoutClick(e));
      }

      if (this.countrySelect) {
        this.countrySelect.addEventListener('change', () => this.populateProvinces());
      }

      this.inputs.forEach((input) => {
        input.addEventListener('input', () => this.clearFieldError(input));
      });

      if (this.retryBtn) {
        this.retryBtn.addEventListener('click', () => this.handleSaveFlow(true));
      }

      if (this.continueWithoutBtn) {
        this.continueWithoutBtn.addEventListener('click', () => this.onContinueWithout());
      }

      if (this.checkbox.checked) {
        this.savedSnapshot = this.buildSnapshot();
      }
    }

    // ---- Country / province ----
    initProvinces() {
      if (!this.countrySelect) return;
      const selectedCountry = this.dataset.selectedCountry || 'Spain';
      const hasMatch = Array.prototype.some.call(this.countrySelect.options, (o) => o.value === selectedCountry);
      if (hasMatch) this.countrySelect.value = selectedCountry;
      this.populateProvinces(this.dataset.selectedProvince || '');
    }

    populateProvinces(preselect) {
      if (!this.countrySelect || !this.provinceSelect) return;
      const option = this.countrySelect.options[this.countrySelect.selectedIndex];
      let provinces = [];
      try {
        provinces = option && option.dataset.provinces ? JSON.parse(option.dataset.provinces) : [];
      } catch (e) {
        provinces = [];
      }
      this.provinceSelect.innerHTML = '';
      if (!provinces.length) {
        if (this.provinceWrapper) this.provinceWrapper.hidden = true;
        this.provinceSelect.setCustomValidity('');
        return;
      }
      if (this.provinceWrapper) this.provinceWrapper.hidden = false;
      provinces.forEach((p) => {
        const opt = document.createElement('option');
        opt.value = p[1];
        opt.textContent = p[0];
        this.provinceSelect.appendChild(opt);
      });
      if (preselect && provinces.some((p) => p[1] === preselect)) {
        this.provinceSelect.value = preselect;
      }
    }

    // ---- Toggle ----
    onToggle() {
      if (this.checkbox.checked) {
        this.fieldset.hidden = false;
        this.savedSnapshot = null;
      } else {
        this.fieldset.hidden = true;
        this.hideSaveUi();
        this.clearAllFieldErrors();
        this.savedSnapshot = null;
        this.clearAttributesInBackground();
      }
    }

    clearAttributesInBackground() {
      const payload = {};
      this.ATTR_KEYS.forEach((k) => { payload[k] = ''; });
      this.sendCartUpdate(payload).catch(() => { /* best-effort; re-checked on next checkout attempt if the box is ticked again */ });
    }

    // ---- Validation ----
    isSpain() {
      return this.countrySelect ? this.countrySelect.value === 'Spain' : true;
    }

    validateTaxIdSpain(raw) {
      const value = (raw || '').toUpperCase().replace(/[\s-]/g, '');
      if (!value) return false;
      const letters = 'TRWAGMYFPDXBNJZSQVHLCKE';
      const nifRe = /^[0-9]{8}[A-Z]$/;
      const nieRe = /^[XYZ][0-9]{7}[A-Z]$/;
      if (nifRe.test(value)) {
        const num = parseInt(value.slice(0, 8), 10);
        return value[8] === letters[num % 23];
      }
      if (nieRe.test(value)) {
        const prefixMap = { X: '0', Y: '1', Z: '2' };
        const num = parseInt(prefixMap[value[0]] + value.slice(1, 8), 10);
        return value[8] === letters[num % 23];
      }
      const cifRe = /^[ABCDEFGHJNPQRSUVW][0-9]{7}[0-9A-J]$/;
      if (cifRe.test(value)) {
        const orgLetter = value[0];
        const digits = value.slice(1, 8);
        let sum = 0;
        for (let i = 0; i < digits.length; i++) {
          const d = parseInt(digits[i], 10);
          if (i % 2 === 0) {
            let doubled = d * 2;
            if (doubled > 9) doubled -= 9;
            sum += doubled;
          } else {
            sum += d;
          }
        }
        const controlDigit = (10 - (sum % 10)) % 10;
        const controlLetter = 'JABCDEFGHI'[controlDigit];
        const last = value[8];
        const lettersOnly = ['K', 'P', 'Q', 'S'];
        const numbersOnly = ['A', 'B', 'E', 'H'];
        if (lettersOnly.indexOf(orgLetter) > -1) return last === controlLetter;
        if (numbersOnly.indexOf(orgLetter) > -1) return last === String(controlDigit);
        return last === String(controlDigit) || last === controlLetter;
      }
      return false;
    }

    validateTaxIdGeneric(raw) {
      const value = (raw || '').trim();
      return /^[A-Za-z0-9\s\-.\/]{4,20}$/.test(value);
    }

    validate() {
      let valid = true;
      const spain = this.isSpain();

      this.inputs.forEach((input) => {
        if (input.dataset.optional === 'true') {
          this.clearFieldError(input);
          return;
        }
        const value = input.value.trim();
        const key = input.dataset.key;
        let message = '';

        if (!value) {
          message = (window.theme && theme.strings.invoiceRequired) || 'Este campo es obligatorio';
        } else if (key === 'invoice_tax_id') {
          const ok = spain ? this.validateTaxIdSpain(value) : this.validateTaxIdGeneric(value);
          if (!ok) message = (window.theme && theme.strings.invoiceTaxId) || 'Revisa el NIF/CIF, no parece válido';
        }

        this.setFieldMessage(input, message);
        if (message) valid = false;
      });

      if (this.provinceSelect && this.provinceWrapper && !this.provinceWrapper.hidden && !this.provinceSelect.value) {
        valid = false;
        this.provinceSelect.setCustomValidity((window.theme && theme.strings.invoiceRequired) || 'Este campo es obligatorio');
      } else if (this.provinceSelect) {
        this.provinceSelect.setCustomValidity('');
      }

      return valid;
    }

    setFieldMessage(input, message) {
      input.setCustomValidity(message);
      const errorEl = this.querySelector('#' + input.id + '-error');
      if (errorEl) errorEl.textContent = message;
    }

    clearFieldError(input) {
      this.setFieldMessage(input, '');
    }

    clearAllFieldErrors() {
      this.inputs.forEach((input) => this.clearFieldError(input));
    }

    reportFirstInvalid() {
      let firstInvalid = this.inputs.find((i) => i.validationMessage);
      if (!firstInvalid && this.provinceSelect && this.provinceSelect.validationMessage) {
        firstInvalid = this.provinceSelect;
      }
      if (firstInvalid) {
        firstInvalid.reportValidity();
        firstInvalid.focus();
      }
    }

    // ---- Values / cart I/O ----
    collectValues() {
      const values = { invoice_requested: 'true' };
      this.inputs.forEach((input) => { values[input.dataset.key] = input.value.trim(); });
      values.invoice_country_code = this.countrySelect ? this.countrySelect.value : '';
      values.invoice_province_code = (this.provinceWrapper && this.provinceWrapper.hidden) || !this.provinceSelect
        ? ''
        : this.provinceSelect.value;
      return values;
    }

    buildSnapshot() {
      return JSON.stringify(this.collectValues());
    }

    async sendCartUpdate(attributes) {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes }),
        credentials: 'same-origin',
      });
      if (!res.ok) throw new Error('cart_update_failed');
      return res.json();
    }

    async verifyCartAttributes(expected) {
      const res = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
      if (!res.ok) throw new Error('cart_fetch_failed');
      const cart = await res.json();
      const attrs = cart.attributes || {};
      return Object.keys(expected).every((key) => (expected[key] || '') === (attrs[key] || ''));
    }

    // ---- Save flow ----
    async handleSaveFlow(fromRetry) {
      if (this.isSaving) return;
      this.hideSaveUi();

      if (!this.validate()) {
        this.reportFirstInvalid();
        return;
      }

      const values = this.collectValues();
      const snapshot = JSON.stringify(values);

      if (!fromRetry && this.savedSnapshot === snapshot) {
        this.proceedToCheckout();
        return;
      }

      this.isSaving = true;
      this.setSavingUi(true);

      let success = false;
      for (let attempt = 0; attempt < this.MAX_ATTEMPTS && !success; attempt++) {
        try {
          await this.sendCartUpdate(values);
          success = await this.verifyCartAttributes(values);
        } catch (e) {
          success = false;
        }
      }

      this.isSaving = false;
      this.setSavingUi(false);

      if (success) {
        this.savedSnapshot = snapshot;
        this.proceedToCheckout();
      } else {
        this.showSaveError();
      }
    }

    async onContinueWithout() {
      this.hideSaveUi();
      this.checkbox.checked = false;
      this.fieldset.hidden = true;
      this.savedSnapshot = null;

      const payload = {};
      this.ATTR_KEYS.forEach((k) => { payload[k] = ''; });

      let success = false;
      for (let attempt = 0; attempt < this.MAX_ATTEMPTS && !success; attempt++) {
        try {
          await this.sendCartUpdate(payload);
          success = await this.verifyCartAttributes(payload);
        } catch (e) {
          success = false;
        }
      }

      if (!success) this.showClearWarning();
      this.proceedToCheckout();
    }

    proceedToCheckout() {
      if (!this.checkoutButton) return;
      this.bypassIntercept = true;
      this.checkoutButton.click();
    }

    onCheckoutClick(e) {
      if (this.bypassIntercept) {
        this.bypassIntercept = false;
        return;
      }
      if (!this.checkbox.checked) return;
      e.preventDefault();
      this.handleSaveFlow(false);
    }

    // ---- UI helpers ----
    setSavingUi(isSaving) {
      if (this.retryBtn) this.retryBtn.hidden = true;
      if (this.continueWithoutBtn) this.continueWithoutBtn.hidden = true;
      if (this.saveErrorEl) {
        this.saveErrorEl.hidden = !isSaving;
        this.saveErrorEl.textContent = isSaving ? ((window.theme && theme.strings.invoiceSaving) || 'Guardando los datos de facturación...') : '';
      }
    }

    showSaveError() {
      if (this.saveErrorEl) {
        this.saveErrorEl.hidden = false;
        this.saveErrorEl.textContent = (window.theme && theme.strings.invoiceSaveError) || 'No hemos podido guardar los datos de facturación.';
      }
      if (this.retryBtn) this.retryBtn.hidden = false;
      if (this.continueWithoutBtn) this.continueWithoutBtn.hidden = false;
    }

    showClearWarning() {
      if (this.saveErrorEl) {
        this.saveErrorEl.hidden = false;
        this.saveErrorEl.textContent = (window.theme && theme.strings.invoiceClearError) || 'No hemos podido retirar los datos de facturación.';
      }
    }

    hideSaveUi() {
      if (this.saveErrorEl) {
        this.saveErrorEl.hidden = true;
        this.saveErrorEl.textContent = '';
      }
      if (this.retryBtn) this.retryBtn.hidden = true;
      if (this.continueWithoutBtn) this.continueWithoutBtn.hidden = true;
    }
  }

  customElements.define('invoice-fields', InvoiceFields);
}
